import moment from "moment-timezone"

import {
    AvailablePlanType,
    COMMON_COVERAGE_CONFIG,
    CalculatorResultType,
    CoverageCheckResponseType,
} from "@common-shared/config/coverage.config.ts"
import {
    IOVGetCoverageAgenticData,
    IOVGetCoverageCustomerSpecificData,
    IOVGetCoverageDiscoverData,
    IOVGetCoverageGeneralData,
    IOVGetCoverageMedicaidData,
    IOVGetCoverageSmartScanData,
} from "@common-shared/types/requestData/oneVerifyRequestData.types.ts"
import type {
    IPayer,
    IPractice,
} from "@common-shared/types/responseData/commonResponseData.types.ts"
import {
    IOneVerifyCoverageCheckHistory,
    IOneVerifyCoverageResult,
} from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { API_ROUTES } from "../config/routes.config"
import { HttpHelper } from "../helpers/http.helper"
import { UtilHelper } from "../helpers/util.helper.tsx"
import type { IDynamicCheckerFormInputData } from "../types/data.types.ts"

export class CheckerService {
    /**
     * Run general coverage check
     */
    static async runGeneralCoverageCheck(
        data: IDynamicCheckerFormInputData,
        practice: IPractice,
        willRetry: boolean,
        isInNetworkCheck = false,
    ): Promise<IOneVerifyCoverageCheckHistory | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_GET_COVERAGE_GENERAL.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            CheckerService.mapDynamicFormDataToGeneralCheck(
                data,
                isInNetworkCheck,
            ),
            "POST",
            undefined,
            undefined,
            undefined,
            willRetry ? [400] : undefined,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Run smart scan coverage check
     */
    static async runSmartScanCoverageCheck(
        data: IDynamicCheckerFormInputData,
        practice: IPractice,
        isInNetworkCheck?: boolean,
        ignoreToastErrors = false,
    ): Promise<IOneVerifyCoverageCheckHistory | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_GET_SMART_SCAN_COVERAGE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            CheckerService.mapDynamicFormDataToSmartScanCheck(
                data,
                isInNetworkCheck,
            ),
            "POST",
            undefined,
            undefined,
            undefined,
            ignoreToastErrors ? [404] : undefined,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Run discover coverage check
     */
    static async runDiscoverCoverageCheck(
        data: IDynamicCheckerFormInputData,
        practice: IPractice,
        isInNetworkCheck?: boolean,
        ignoreToastErrors = false,
    ): Promise<IOneVerifyCoverageCheckHistory | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_GET_DISCOVER_COVERAGE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            CheckerService.mapDynamicFormDataToDiscoverCheck(
                data,
                isInNetworkCheck,
            ),
            "POST",
            undefined,
            undefined,
            undefined,
            ignoreToastErrors ? [404] : undefined,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Run medicaid coverage check
     */
    static async runMedicaidCoverageCheck(
        data: IDynamicCheckerFormInputData,
        practice: IPractice,
        willRetry: boolean,
    ): Promise<IOneVerifyCoverageCheckHistory | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_GET_MEDICAID_COVERAGE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            CheckerService.mapDynamicFormDataToMedicaidCheck(data),
            "POST",
            undefined,
            undefined,
            undefined,
            willRetry ? [400] : undefined,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Run agentic phone coverage check
     */
    static async runAgenticPhoneCoverageCheck(
        data: IDynamicCheckerFormInputData,
        practice: IPractice,
        willRetry: boolean,
        isInNetworkCheck = true,
    ): Promise<IOneVerifyCoverageCheckHistory | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_GET_AGENTIC_PHONE_COVERAGE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            CheckerService.mapDynamicFormDataToAgenticCheck(
                data,
                isInNetworkCheck,
            ),
            "POST",
            undefined,
            undefined,
            undefined,
            willRetry ? [400] : undefined,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get default errored request
     */
    static getDefaultErrorCoverageRequest(
        practice: IPractice,
        data: IDynamicCheckerFormInputData,
    ): IOVGetCoverageGeneralData {
        return {
            ...data,

            modalityCode: data.modalityCode,
            providerNpi: data.providerNpi || practice.groupNPI,
            payerId: data.payer?.id || 0,
            dob: UtilHelper.dateToMysqlFormat(moment(data.dob).toDate()),
            sessionCharge: UtilHelper.formatDollarsToCents(
                data.sessionRate ||
                    COMMON_COVERAGE_CONFIG.defaultHealthProviderRate,
            ),
            memberId: data.memberId || "",
        }
    }

    /**
     * Get default errored results
     */
    static getDefaultErrorCoverageResult(
        resultId: string,
        responseType: CoverageCheckResponseType,
        payer: IPayer | null,
        memberId?: string,
        isInNetworkCheck?: boolean,
        resultType = CalculatorResultType.generalError,
        isSmartScanResult = false,
    ): IOneVerifyCoverageResult {
        return {
            resultId,
            responseType,

            memberId: memberId || "",
            payer,
            isInNetworkCheck:
                typeof isInNetworkCheck !== "undefined"
                    ? isInNetworkCheck
                    : true,

            // From ICoverageResult
            coverageQuotaExceeded: false,
            planType: AvailablePlanType.NOT_APPLICABLE,
            planName: null,
            planBeginDate: null,
            possibleMVOBTrigger: false,
            coverageError: null,
            modalityCoverageStatus: null,
            eligibilityBeginDate: null,
            eligibilityEndDate: null,
            relationshipToSubscriber: null,
            override: null,
            flags: [],
            customerPatientType: null,
            customerPatientId: null,
            customerPatientNextAppointmentDate: null,
            thirdPartyPayer: null, // deprecated, keeping for displaying old checks
            thirdPartyAdministrator: null,
            additionalPolicy: null,
            planResetSoon: null,
            resetBenefitsStatus: null,
            resetBenefits: null,
            priorAuthorization: null,
            visitsTotal: null,
            visitsRemaining: null,
            insuranceType: null,
            managedCareOrganization: null,
            eligibilityType: null,
            telehealth: null,
            qmbStatus: null,
            isSmartScanResult,
            medicareAdvantage: null,
            primaryCarePhysician: null,
            unknownFields: [],

            // From ICalculatorResult
            planStatus: null,
            modalityCoverageStatusTernary: null,
            isError: true,
            resultType,
            groupName: null,
            groupId: null,
            copayment: null,
            coinsurance: null,
            deductible: null,
            remainingDeductible: null,
            oopMax: null,
            remainingOopMax: null,
            currentSessionRate: null,
            memberObligation: null,
            postDeductibleMemberObligation: null,
            postDeductiblePayerObligation: null,
            postOopMaxMemberObligation: null,
            postOopMaxPayerObligation: null,
            remainingSessionsBeforeDeductible: null,
            remainingSessionsBeforeOopMax: null,
            planEndDate: null,
            overriddenPayer: null,
            payerObligation: null,
            createdAt: new Date().getTime(),
        }
    }

    /**
     * Map dynamic form data to request data for Smart Scan Coverage Check
     */
    private static mapDynamicFormDataToSmartScanCheck(
        data: IDynamicCheckerFormInputData,
        inNetworkCheckOverride?: boolean,
    ): IOVGetCoverageSmartScanData {
        const {
            id,
            modalityCode,
            firstName,
            lastName,
            dob,
            providerNpi,
            inNetworkCheck,
            sessionRate,
            cptCode,
            zipCode,
            officeId,
        } = data

        return {
            id,
            modalityCode,

            zipCode: zipCode || "",

            firstName,
            lastName,
            dob: UtilHelper.dateToMysqlFormat(moment(dob).toDate()),

            providerNpi,
            // sessionRate is in $, should be converted to cents
            sessionCharge: UtilHelper.formatDollarsToCents(
                sessionRate || COMMON_COVERAGE_CONFIG.defaultHealthProviderRate,
            ),
            cptCode: cptCode || undefined,

            inNetwork:
                typeof inNetworkCheckOverride !== "undefined"
                    ? inNetworkCheckOverride
                    : inNetworkCheck,

            ...CheckerService.mapDynamicFormDataToCommonCustomerData(data),

            officeId: officeId || undefined,

            isBulk: data.isBulk,
        }
    }

    /**
     * Map dynamic form data to request data for Discover Coverage Check
     */
    private static mapDynamicFormDataToDiscoverCheck(
        data: IDynamicCheckerFormInputData,
        inNetworkCheckOverride?: boolean,
    ): IOVGetCoverageDiscoverData {
        return {
            ...CheckerService.mapDynamicFormDataToSmartScanCheck(
                data,
                inNetworkCheckOverride,
            ),

            payerId: data.payer?.id || -1,
        }
    }

    /**
     * Map dynamic form data to request data for General Coverage Check
     */
    private static mapDynamicFormDataToGeneralCheck(
        data: IDynamicCheckerFormInputData,
        inNetworkCheckOverride?: boolean,
    ): IOVGetCoverageGeneralData {
        const { zipCode: _zipCode, ...rest } =
            CheckerService.mapDynamicFormDataToDiscoverCheck(
                data,
                inNetworkCheckOverride,
            )

        return {
            ...rest,

            memberId: data.memberId || "",
            officeId: data.officeId || undefined,
        }
    }

    /**
     * Map dynamic form data to request data for Agentic Coverage Check
     * In the future for agentic portal might be different
     */
    private static mapDynamicFormDataToAgenticCheck(
        data: IDynamicCheckerFormInputData,
        inNetworkCheckOverride?: boolean,
    ): IOVGetCoverageAgenticData {
        return {
            ...CheckerService.mapDynamicFormDataToGeneralCheck(
                data,
                inNetworkCheckOverride,
            ),

            providerTaxId: data.providerTaxId || "",
            renderingProviderName: data.renderingProviderName || "",
        }
    }

    /**
     Map dynamic form data to request data for Medicaid Coverage Check
     */
    private static mapDynamicFormDataToMedicaidCheck(
        data: IDynamicCheckerFormInputData,
    ): IOVGetCoverageMedicaidData {
        const {
            id,
            modalityCode,
            firstName,
            lastName,
            dob,
            providerNpi,
            state,
            cptCode,
            memberId,
            memberGender,
            officeId,
        } = data

        return {
            id,
            modalityCode,

            firstName,
            lastName,
            dob: UtilHelper.dateToMysqlFormat(moment(dob).toDate()),

            providerNpi,
            state: state || "",
            cptCode: cptCode || undefined,
            memberId: memberId || undefined,
            memberGender: memberGender || undefined,

            ...CheckerService.mapDynamicFormDataToCommonCustomerData(data),

            officeId: officeId || undefined,

            isBulk: data.isBulk,
        }
    }

    /**
     * Map dynamic form data to request data to customer specific data
     */
    private static mapDynamicFormDataToCommonCustomerData(
        data: IDynamicCheckerFormInputData,
    ): IOVGetCoverageCustomerSpecificData {
        return {
            customerPatientId: data.customerPatientId || undefined,
            customerPatientType: data.customerPatientType || undefined,
            customerPatientNextAppointmentDate:
                data.customerPatientNextAppointmentDate
                    ? UtilHelper.dateToMysqlFormat(
                          moment(
                              data.customerPatientNextAppointmentDate,
                          ).toDate(),
                      )
                    : undefined,
        }
    }
}
